import { ACCENT_SCHEMES } from "@/modules/user-settings/_constants/themes";
import { AccentScheme, Theme, userConfigStore } from "@/store/user-config-store";
import { useMemo, useSyncExternalStore } from "react";

export type ResolvedMapThemeMode = "light" | "dark";

export type RgbColor = [number, number, number];
export type RgbaColor = [number, number, number, number];

export interface MapThemePalette {
  mode: ResolvedMapThemeMode;
  basemapStyleUrl: string;
  accent: {
    primary: string;
    secondary: string;
    primaryRgb: RgbColor;
    secondaryRgb: RgbColor;
  };
  surface: {
    panelBackground: string;
    tooltipBackground: string;
    border: string;
    separator: string;
    shadow: string;
    mapBackground: string;
  };
  text: {
    primary: string;
    secondary: string;
    muted: string;
  };
  icon: {
    accent: string;
    muted: string;
  };
  branch: {
    fill: RgbaColor;
    stroke: RgbaColor;
    glow: RgbaColor;
  };
  route: {
    baseRgb: RgbColor;
    targetRgb: RgbColor;
    headRgb: RgbColor;
    trailRgb: RgbColor;
    glowRgb: RgbColor;
  };
}

export const DARK_MAP_STYLE = "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json";
export const LIGHT_MAP_STYLE = "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";

interface CreateMapThemePaletteArgs {
  mode: ResolvedMapThemeMode;
  accentScheme: AccentScheme;
}

export function resolveMapThemeMode(theme: Theme, systemMode: ResolvedMapThemeMode): ResolvedMapThemeMode {
  if (theme === Theme.System) {
    return systemMode;
  }

  return theme === Theme.Dark ? "dark" : "light";
}

export function rgbCss([red, green, blue]: RgbColor): string {
  return `rgb(${red} ${green} ${blue})`;
}

export function rgbaCss([red, green, blue]: RgbColor, alpha: number): string {
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export function withDeckAlpha([red, green, blue]: RgbColor, alpha: number): RgbaColor {
  return [red, green, blue, alpha];
}

export function createMapThemePalette({ mode, accentScheme }: CreateMapThemePaletteArgs): MapThemePalette {
  const schemeDefinition = ACCENT_SCHEMES.find((scheme) => scheme.scheme === accentScheme) ?? ACCENT_SCHEMES[0];
  const colorSource =
    mode === "dark"
      ? (schemeDefinition.primaryColorDark ?? schemeDefinition.primaryColor)
      : schemeDefinition.primaryColor;
  const primaryRgb = hslStringToRgb(colorSource);
  const secondaryRgb = hslStringToRgb(colorSource);

  const branchFill =
    mode === "dark" ? mixRgb(primaryRgb, [255, 255, 255], 0.78) : mixRgb(primaryRgb, [255, 255, 255], 0.1);
  const routeHeadRgb = mixRgb(primaryRgb, [255, 255, 255], mode === "dark" ? 0.28 : 0.16);
  const routeGlowRgb = mixRgb(secondaryRgb, [255, 255, 255], mode === "dark" ? 0.12 : 0.06);

  return {
    mode,
    basemapStyleUrl: mode === "dark" ? DARK_MAP_STYLE : LIGHT_MAP_STYLE,
    accent: {
      primary: rgbCss(primaryRgb),
      secondary: rgbCss(secondaryRgb),
      primaryRgb,
      secondaryRgb,
    },
    surface: {
      panelBackground: "color-mix(in srgb, var(--card) 88%, transparent)",
      tooltipBackground: "color-mix(in srgb, var(--popover) 94%, transparent)",
      border: "color-mix(in srgb, var(--border) 92%, transparent)",
      separator: "var(--border)",
      shadow:
        mode === "dark"
          ? "0 24px 48px -24px color-mix(in srgb, var(--foreground) 16%, transparent)"
          : "0 24px 48px -24px color-mix(in srgb, var(--primary) 20%, transparent)",
      mapBackground: "var(--background)",
    },
    text: {
      primary: "var(--foreground)",
      secondary: "color-mix(in srgb, var(--foreground) 72%, var(--muted-foreground))",
      muted: "var(--muted-foreground)",
    },
    icon: {
      accent: "var(--primary)",
      muted: "var(--muted-foreground)",
    },
    branch: {
      fill: withDeckAlpha(branchFill, mode === "dark" ? 220 : 235),
      stroke: withDeckAlpha(primaryRgb, mode === "dark" ? 185 : 215),
      glow: withDeckAlpha(secondaryRgb, mode === "dark" ? 48 : 36),
    },
    route: {
      baseRgb: primaryRgb,
      targetRgb: secondaryRgb,
      headRgb: routeHeadRgb,
      trailRgb: secondaryRgb,
      glowRgb: routeGlowRgb,
    },
  };
}

export function useMapTheme(): MapThemePalette {
  const theme = userConfigStore((state) => state.theme);
  const lightAccentScheme = userConfigStore((state) => state.lightAccentScheme);
  const darkAccentScheme = userConfigStore((state) => state.darkAccentScheme);

  const systemMode = useSyncExternalStore(subscribeToSystemThemeMode, getSystemThemeMode, getServerSystemThemeMode);

  return useMemo(() => {
    const mode = resolveMapThemeMode(theme, systemMode);
    const accentScheme = mode === "light" ? lightAccentScheme : darkAccentScheme;

    return createMapThemePalette({
      mode,
      accentScheme,
    });
  }, [darkAccentScheme, lightAccentScheme, systemMode, theme]);
}

const SYSTEM_DARK_MODE_QUERY = "(prefers-color-scheme: dark)";

function subscribeToSystemThemeMode(onChange: () => void): () => void {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const mediaQuery = window.matchMedia(SYSTEM_DARK_MODE_QUERY);
  mediaQuery.addEventListener("change", onChange);
  return () => mediaQuery.removeEventListener("change", onChange);
}

function getServerSystemThemeMode(): ResolvedMapThemeMode {
  return "dark";
}

function getSystemThemeMode(): ResolvedMapThemeMode {
  if (typeof window === "undefined") {
    return "dark";
  }

  return window.matchMedia(SYSTEM_DARK_MODE_QUERY).matches ? "dark" : "light";
}

function mixRgb(base: RgbColor, target: RgbColor, targetRatio: number): RgbColor {
  return [
    Math.round(base[0] * (1 - targetRatio) + target[0] * targetRatio),
    Math.round(base[1] * (1 - targetRatio) + target[1] * targetRatio),
    Math.round(base[2] * (1 - targetRatio) + target[2] * targetRatio),
  ];
}

function hslStringToRgb(value: string): RgbColor {
  const match = value.match(/hsl\(\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%\s*\)/i);

  if (!match) {
    return [0, 0, 0];
  }

  return hslToRgb(Number(match[1]), Number(match[2]), Number(match[3]));
}

function hslToRgb(hue: number, saturation: number, lightness: number): RgbColor {
  const normalizedSaturation = saturation / 100;
  const normalizedLightness = lightness / 100;
  const chroma = (1 - Math.abs(2 * normalizedLightness - 1)) * normalizedSaturation;
  const huePrime = hue / 60;
  const secondComponent = chroma * (1 - Math.abs((huePrime % 2) - 1));
  const matchLightness = normalizedLightness - chroma / 2;

  let redPrime = 0;
  let greenPrime = 0;
  let bluePrime = 0;

  if (huePrime >= 0 && huePrime < 1) {
    redPrime = chroma;
    greenPrime = secondComponent;
  } else if (huePrime >= 1 && huePrime < 2) {
    redPrime = secondComponent;
    greenPrime = chroma;
  } else if (huePrime >= 2 && huePrime < 3) {
    greenPrime = chroma;
    bluePrime = secondComponent;
  } else if (huePrime >= 3 && huePrime < 4) {
    greenPrime = secondComponent;
    bluePrime = chroma;
  } else if (huePrime >= 4 && huePrime < 5) {
    redPrime = secondComponent;
    bluePrime = chroma;
  } else {
    redPrime = chroma;
    bluePrime = secondComponent;
  }

  return [
    Math.round((redPrime + matchLightness) * 255),
    Math.round((greenPrime + matchLightness) * 255),
    Math.round((bluePrime + matchLightness) * 255),
  ];
}
